/* Canonical plan catalogue. The DB `Plan` table is seeded from this array and the
   marketing pricing page renders it via GET /api/v1/billing/plans.
   Prices are 10 % below Metricool (captured 2026-08-06). See docs/PRICING.md.
   Never redefine a price anywhere else. */

export type PlanTier = 'FREE' | 'STARTER' | 'ADVANCED' | 'WHITELABEL' | 'CUSTOM'
export type BillingInterval = 'monthly' | 'annual'

export interface PlanFeatures {
  scheduledPostsPerMonth: number | null // null = unlimited
  analyticsHistoryDays: number | null
  competitorProfiles: number
  teamMembers: number | null
  roles: boolean
  approvals: boolean
  reportTemplates: boolean
  publicApi: boolean
  whitelabel: boolean
  aiCreditsPerBrand: number
  /** Providers excluded from this plan; empty means all launch providers. */
  excludedProviders: readonly string[]
}

export interface PlanDefinition {
  code: string
  name: string
  tier: PlanTier
  brandLimit: number
  priceMonthlyCents: number
  priceAnnualCents: number // per month, billed annually
  currency: 'eur'
  extraPackCents?: number
  extraPackBrands?: number
  /**
   * Per-brand metered rate in cents. Its **presence is the canonical "this plan is billed per
   * registered brand" test** — never the plan code or the tier. A metered plan carries no flat
   * fee and sells no packs: the whole bill is `brandsUsed × pricePerBrandCents`, reported to a
   * Stripe Billing Meter. Monthly only; see `monthlyChargeCents` below.
   */
  pricePerBrandCents?: number
  /**
   * Second-tier per-brand rate in cents, for a metered plan with tiered pricing. Requires
   * `tierThresholdBrands`. Every brand beyond the threshold bills at this rate instead of
   * `pricePerBrandCents` — the first `tierThresholdBrands` brands always bill at the latter.
   */
  secondaryPricePerBrandCents?: number
  /** Brand count at which `secondaryPricePerBrandCents` takes over from `pricePerBrandCents`. */
  tierThresholdBrands?: number
  features: PlanFeatures
  sortOrder: number
  /** Shown as the recommended card on the pricing page. */
  highlighted?: boolean
  tagline: string
}

const base: PlanFeatures = {
  scheduledPostsPerMonth: null,
  analyticsHistoryDays: null,
  competitorProfiles: 100,
  teamMembers: 3,
  roles: false,
  approvals: false,
  reportTemplates: true,
  publicApi: false,
  whitelabel: false,
  aiCreditsPerBrand: 20,
  excludedProviders: [],
}

export const PLANS: readonly PlanDefinition[] = [
  {
    code: 'free',
    name: 'Free',
    tier: 'FREE',
    brandLimit: 1,
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    currency: 'eur',
    sortOrder: 0,
    tagline: 'One brand, the essentials, no card.',
    features: {
      ...base,
      scheduledPostsPerMonth: 20,
      analyticsHistoryDays: 30,
      competitorProfiles: 5,
      teamMembers: 1,
      reportTemplates: false,
      aiCreditsPerBrand: 5,
      excludedProviders: ['linkedin', 'x'],
    },
  },
  {
    code: 'starter_5',
    name: 'Starter',
    tier: 'STARTER',
    brandLimit: 5,
    priceMonthlyCents: 2300,
    priceAnnualCents: 1800,
    currency: 'eur',
    sortOrder: 1,
    tagline: 'For a freelancer running a handful of accounts.',
    features: { ...base },
  },
  {
    code: 'starter_10',
    name: 'Starter',
    tier: 'STARTER',
    brandLimit: 10,
    priceMonthlyCents: 4200,
    priceAnnualCents: 3330,
    currency: 'eur',
    sortOrder: 2,
    tagline: 'Twice the brands, same workflow.',
    features: { ...base },
  },
  {
    code: 'advanced_15',
    name: 'Advanced',
    tier: 'ADVANCED',
    brandLimit: 15,
    priceMonthlyCents: 6100,
    priceAnnualCents: 4860,
    currency: 'eur',
    sortOrder: 3,
    highlighted: true,
    tagline: 'Roles, approvals and client reporting.',
    features: {
      ...base,
      teamMembers: 15,
      roles: true,
      approvals: true,
      publicApi: true,
      aiCreditsPerBrand: 35,
    },
  },
  {
    code: 'advanced_25',
    name: 'Advanced',
    tier: 'ADVANCED',
    brandLimit: 25,
    priceMonthlyCents: 9800,
    priceAnnualCents: 7830,
    currency: 'eur',
    sortOrder: 4,
    tagline: 'For an agency with a full client roster.',
    features: {
      ...base,
      teamMembers: 15,
      roles: true,
      approvals: true,
      publicApi: true,
      aiCreditsPerBrand: 35,
    },
  },
  {
    code: 'advanced_50',
    name: 'Advanced',
    tier: 'ADVANCED',
    brandLimit: 50,
    priceMonthlyCents: 19400,
    priceAnnualCents: 15480,
    currency: 'eur',
    sortOrder: 5,
    tagline: 'Fifty brands, one console.',
    features: {
      ...base,
      teamMembers: 15,
      roles: true,
      approvals: true,
      publicApi: true,
      aiCreditsPerBrand: 35,
    },
  },
  {
    code: 'whitelabel_50',
    name: 'White label',
    tier: 'WHITELABEL',
    brandLimit: 50,
    priceMonthlyCents: 24950, // 50 × €4.99
    priceAnnualCents: 24950,
    currency: 'eur',
    extraPackCents: 11475, // 25 × €4.59
    extraPackBrands: 25,
    sortOrder: 6,
    tagline: 'Your logo, your domain, your product.',
    features: {
      ...base,
      teamMembers: null,
      roles: true,
      approvals: true,
      publicApi: true,
      whitelabel: true,
      aiCreditsPerBrand: 50,
    },
  },
  {
    /* Sales-gated and metered. There is no checkout button for this plan: a superadmin assigns it
       after a sales conversation, and Stripe invoices `brandsUsed × pricePerBrandCents` each month
       from a Billing Meter. `brandLimit` is a sentinel, not a cap — the plan is priced per unit, so
       the allowance machinery in `brandAllowance()` must never be what stops a customer. */
    code: 'custom',
    name: 'Custom',
    tier: 'CUSTOM',
    brandLimit: 1_000_000,
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    currency: 'eur',
    pricePerBrandCents: 359, // tier 1 — the first 100 brands
    secondaryPricePerBrandCents: 340, // tier 2 — every brand beyond the threshold
    tierThresholdBrands: 100,
    sortOrder: 7,
    tagline: 'Priced per brand, set up by our team.',
    features: {
      ...base,
      teamMembers: null,
      roles: true,
      approvals: true,
      publicApi: true,
      /* Deliberately off: white-labelling is a separate axis from per-brand pricing. A deal that
         needs both gets the flag flipped through PATCH /admin/plans/:code. */
      whitelabel: false,
      aiCreditsPerBrand: 35,
      excludedProviders: [],
    },
  },
]

export const PLAN_BY_CODE: Record<string, PlanDefinition> = Object.fromEntries(
  PLANS.map((p) => [p.code, p]),
)

/** Effective brand allowance: plan limit + stacked packs, unless a superadmin overrode it. */
export const brandAllowance = (
  planCode: string,
  extraBrandPacks = 0,
  override?: number | null,
): number => {
  if (override != null) return override
  const plan = PLAN_BY_CODE[planCode]
  if (!plan) return 0
  return plan.brandLimit + extraBrandPacks * (plan.extraPackBrands ?? 0)
}

/**
 * Monthly charge in cents for a plan + interval + packs. Annual bills 12 × the monthly rate.
 *
 * A **metered** plan (`pricePerBrandCents` set) is priced per registered brand instead: it has no
 * flat fee, no packs and no annual cycle, so the interval and the pack count are both ignored and
 * the answer is `brandsUsed × pricePerBrandCents` — or, when `tierThresholdBrands` and
 * `secondaryPricePerBrandCents` are both set, the first `tierThresholdBrands` brands at
 * `pricePerBrandCents` and every brand beyond that at the secondary rate. A caller with no brand
 * count to hand gets €0, which is the honest estimate — there is no meaningful flat price to show
 * without a customer.
 */
export const monthlyChargeCents = (
  planCode: string,
  interval: BillingInterval,
  extraBrandPacks = 0,
  brandsUsed?: number,
): number => {
  const plan = PLAN_BY_CODE[planCode]
  if (!plan) return 0
  if (plan.pricePerBrandCents != null) {
    const used = brandsUsed ?? 0
    if (plan.tierThresholdBrands != null && plan.secondaryPricePerBrandCents != null) {
      const tier1 = Math.min(used, plan.tierThresholdBrands)
      const tier2 = Math.max(0, used - plan.tierThresholdBrands)
      return tier1 * plan.pricePerBrandCents + tier2 * plan.secondaryPricePerBrandCents
    }
    return used * plan.pricePerBrandCents
  }
  const unit = interval === 'annual' ? plan.priceAnnualCents : plan.priceMonthlyCents
  return unit + extraBrandPacks * (plan.extraPackCents ?? 0)
}

export const formatEur = (cents: number): string =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)
